import '.a{display:flex;}.ssr-plugin'
import '.b2{gap:16px;}.ssr-plugin'
import '.i{flex-direction:column;}.ssr-plugin'
import '.h{width:100%;}.ssr-plugin'
import '.bk{padding-bottom:16px;}.ssr-plugin'
import '.bl{display:grid;}.ssr-plugin'
import '.b4{padding-bottom:48px;}.ssr-plugin'
import '.bm{align-items:stretch;}.ssr-plugin'
import { FC } from 'react'
import { Layout } from './Layout'
import { styled } from 'inlines'
import { Navigation } from '../components/Navigation'
import { Text } from '../components/Text'
import { useAppCtx } from '../providers/useAppCtx'
import { ExpandableText } from '../components/ExpandableText'
import { ContestantCard } from '../components/ContestantCard'
import { useIsMobile } from '../hooks/useIsMobile'
import { CartBar } from '../components/CartBar'
import { useContentQuery } from '../hooks/useContentQuery'
import { useQuery } from '@based/sdk'
// @ts-ignore
import fallbackImage from '../assets/img/Square-Heart.png'
import { useTranslations } from '../hooks/useTranslations'
import { useScrollToTop } from '../hooks/useScrollToTop'
import { RecapVideo } from '../components/RecapVideo'
import useLocalStorage from '@based/use-local-storage'

export type ContestantsProps = {
  'aria-label'?: {
    nav: string
    footer: string
  }
}

export const Contestants: FC<ContestantsProps> = ({
  'aria-label': ariaLabelProp = {
    nav: 'Main navigation',
    footer: 'Footer navigation',
  },
}) => {
  const { languages, votes, setVotes, votesCount } = useAppCtx()
  const { data: sequence = {} } = useContentQuery('sequence')
  // @ts-ignore
  const edition = window?.DATA?.edition
  const { data: config = {} } = useQuery(
    edition !== undefined ? 'edition-config' : '',
    {
      editionId: edition,
    },
  )

  const { data: dictionary = {} } = useTranslations('voting')
  const { data: contestantDictionary = {} } =
    useTranslations('contestant_content')

  const isMobile = useIsMobile()

  useScrollToTop()

  const toggleContent = [
    dictionary?.expand_rules_button,
    dictionary?.collapse_rules_button,
  ]
  const contestants = sequence?.round.contestants || []
  const { price, currency, maxVotes } = config
  const remainingVotes = maxVotes - votesCount

  const hasRecapVideo = Boolean(
    sequence?.recap_video?.src || sequence?.recap_video?.hls,
  )
  const recapId = `recap:${sequence?.id}`
  const [watchedRecap, setWatchedRecap] = useLocalStorage<boolean>(
    recapId,
    false,
  )

  return !watchedRecap && hasRecapVideo ? (
    <RecapVideo
      src={sequence?.recap_video?.hls || sequence?.recap_video?.src}
      setWatchedRecap={setWatchedRecap}
    />
  ) : (
    <Layout
      style={{
        justifyContent: 'start',
        gap: 48,
        ...(isMobile
          ? {
              padding: 24,
            }
          : {
              padding: '48px 24px',
              alignSelf: 'center',
            }),
      }}
    >
      <Layout.Navbar>
        <Navigation
          as="nav"
          aria-label={ariaLabelProp.nav}
          isCartVisible
          isExpandable
          cartItems={votesCount}
          languages={languages}
        />
      </Layout.Navbar>

      <Layout.Top>
        <div className="a b2 i h"
        >
          <Text color={'gradient'} variant="title" isMultiline>
            {dictionary?.voting_prompt}
          </Text>
          <ExpandableText
            visibleContent={dictionary?.short_voting_rules}
            hiddenContent={dictionary?.short_payment_rules}
            label={toggleContent}
          />
        </div>
      </Layout.Top>

      <Layout.Middle>
        <div className="h bk">
          <div className="h bl b4 bm"
            style={{
              gridTemplateColumns: isMobile
                ? 'repeat(auto-fit, minmax(136px, 1fr))'
                : 'repeat(auto-fit, minmax(174px, 1fr))',
              // gridAutoFlow: 'dense',
              gap: isMobile ? 16 : 32,}}
          >
            {contestants
              .filter((contestant) => !contestant?.$disqualified)
              ?.sort((a, b) => Number(a.$ddi) - Number(b.$ddi))
              ?.map((contestant, index) => {
                const currentVotes = votes?.[contestant.$ddi] || 0
                const maxForThis = currentVotes + remainingVotes

                const bio = {
                  about: contestantDictionary?.about,
                  bio: contestant?.bio,
                  lyricsTitle: contestantDictionary?.lyrics,
                  lyrics: contestant?.lyrics,
                }

                return (
                  <ContestantCard
                    key={contestant.id + '-' + contestant.name + '-' + index}
                    isInverted={currentVotes === 0}
                    label={contestant.name}
                    description={contestant.song}
                    votePrice={price}
                    currency={currency}
                    image={contestant.image?.src || fallbackImage}
                    votes={currentVotes}
                    isFocused={currentVotes > 0}
                    maxVotes={maxForThis}
                    ddi={contestant.$ddi}
                    bio={bio}
                    on={{
                      change: (newVotesForThis) => {
                        const parseVotes = (prev) => {
                          if (newVotesForThis === 0 && prev) {
                            const { [contestant.$ddi]: _, ...rest } = prev
                            return rest
                          }
                          return {
                            ...prev,
                            [contestant.$ddi]: newVotesForThis,
                          }
                        }
                        setVotes(parseVotes(votes))
                      },
                    }}
                    style={{ maxWidth: 176 }}
                  />
                )
              })}
          </div>

          <Text color="gradient" variant="title" isMultiline>
            {dictionary?.voting_banner}
          </Text>
        </div>
      </Layout.Middle>

      <Layout.Footer>
        <Navigation
          as="footer"
          aria-label={ariaLabelProp.footer}
          languages={languages}
          style={{ marginBottom: votesCount > 0 ? 96 : 0 }}
        />

        {votesCount > 0 && <CartBar />}
      </Layout.Footer>
    </Layout>
  )
}
